import React, { useEffect, useCallback, useState, useMemo } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { fetchEv_list } from '../../store';
import config from '../../config';
import { Line } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { ko } from 'date-fns/locale';
import { format } from 'date-fns';
import { Card, Space } from 'antd';
import { CSVLink } from 'react-csv';

ChartJS.register(
  Tooltip,
  Legend,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Title
);

function labelToMinutes(label) {
  const [h, m] = label.split(':').map(Number);
  return h * 60 + m;
}

function findClosestTimeData(dataArray, targetLabel, maxDiffMinutes = 10) {
  if (!dataArray || dataArray.length === 0) return null;
  let closest = null;
  let minDiff = Infinity;
  dataArray.forEach(item => {
    const date = new Date(item.created_at);
    const itemLabel = date.toTimeString().substring(0, 5);
    const diff = Math.abs(labelToMinutes(itemLabel) - labelToMinutes(targetLabel));
    if (diff <= maxDiffMinutes && diff < minDiff) {
      minDiff = diff;
      closest = item;
    }
  });
  return closest;
}

const EvMeasurementLineChart = () => {
  const dispatch = useDispatch();
  const Ev_list = useSelector((state) => state.Ev_list || {});
  const [chartData, setChartData] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isEvReady, setIsEvReady] = useState(false);
  const [isEvDataReady, setIsEvDataReady] = useState(false);

  const labels = useMemo(() => {
    const result = [];
    for (let h = 5; h <= 20; h++) {
      for (let m = 0; m < 60; m += 10) {
        const hour = h.toString().padStart(2, '0');
        const minute = m.toString().padStart(2, '0');
        result.push(`${hour}:${minute}`);
      }
    }
    return result;
  }, []);

  useEffect(() => {
    dispatch(fetchEv_list());
  }, [dispatch]);

  useEffect(() => {
    if (Ev_list.seq && Ev_list.seq.length > 0) setIsEvReady(true);
  }, [Ev_list]);

  const fetchData = useCallback(async () => {
    try {
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await fetch(`${config.backendUrl}/get_hourly_Ev_mrt_data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: selectedDateStr })
      });
      const { data } = await res.json();
      const seqData = {};
      data.forEach(item => {
        if (!seqData[item.seq]) seqData[item.seq] = [];
        seqData[item.seq].push(item);
      });

      const horizontalIrradiance = new Array(labels.length).fill(null);
      const tiltedIrradiance = new Array(labels.length).fill(null);
      const surfaceTemp = new Array(labels.length).fill(null);
      const outdoorTemp = new Array(labels.length).fill(null);

      const seqs = Ev_list.seq || [];
      const sunTypes = Ev_list.ev_sunType || [];
      const tempTypes = Ev_list.ev_temperatureType || [];

      labels.forEach((label, idx) => {
        seqs.forEach((seq, index) => {
          const closest = findClosestTimeData(seqData[seq], label);
          if (!closest) return;
          const sunType = sunTypes[index];
          const tempType = tempTypes[index];
          if (sunType === 0 && horizontalIrradiance[idx] === null) horizontalIrradiance[idx] = closest.sun;
          if (sunType === 1 && tiltedIrradiance[idx] === null) tiltedIrradiance[idx] = closest.sun;
          if (tempType === 0 && surfaceTemp[idx] === null) surfaceTemp[idx] = closest.temperature / 10;
          if (tempType === 1 && outdoorTemp[idx] === null) outdoorTemp[idx] = closest.temperature / 10;
        });
      });

      const newChartData = labels.map((label, i) => ({
        time: label,
        horizontalIrradiance: horizontalIrradiance[i],
        tiltedIrradiance: tiltedIrradiance[i],
        surfaceTemp: surfaceTemp[i],
        outdoorTemp: outdoorTemp[i]
      }));

      setChartData(newChartData);
      setIsEvDataReady(horizontalIrradiance.some(x => x !== null));
    } catch (err) {
      console.error('환경센서 데이터 에러:', err);
    }
  }, [Ev_list, labels, selectedDate]);

  useEffect(() => {
    if (!isEvReady) return;
    fetchData();
    const interval = setInterval(fetchData, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData, isEvReady]);

  const upperData = {
    labels,
    datasets: [
      {
        label: '수평 일사량',
        data: chartData.map(d => d.horizontalIrradiance),
        borderColor: 'rgba(255,99,132,1)',
        backgroundColor: 'rgba(255,99,132,0.2)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 3,
      },
      {
        label: '경사 일사량',
        data: chartData.map(d => d.tiltedIrradiance),
        borderColor: 'rgba(54,162,235,1)',
        backgroundColor: 'rgba(54,162,235,0.2)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 3,
      },
    ]
  };

  const lowerData = {
    labels,
    datasets: [
      {
        label: '외기 온도',
        data: chartData.map(d => d.outdoorTemp),
        borderColor: 'rgba(255,206,86,1)',
        backgroundColor: 'rgba(255,206,86,0.2)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 3,
      },
      {
        label: '표면 온도',
        data: chartData.map(d => d.surfaceTemp),
        borderColor: 'rgba(75,192,192,1)',
        backgroundColor: 'rgba(75,192,192,0.2)',
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 3,
      },
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { position: 'top', labels: { usePointStyle: true } },
      tooltip: {
        callbacks: {
          label: ctx => `${ctx.dataset.label}: ${ctx.raw ?? '-'} `
        }
      }
    },
    scales: {
      x: {
        type: 'category',
        ticks: { font: { size: 10 } },
        grid: { display: false }
      },
      y: {
        type: 'linear',
        beginAtZero: true,
        ticks: { font: { size: 10 } },
         grid: {
          display: true,
          color: 'rgba(0,0,0,0.05)',
          drawBorder: false,
          drawTicks: false,
          lineWidth: 1
        }
      }
    }
  };

  return (
    <Card style={{ margin: '10px' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space>
          <DatePicker
            selected={selectedDate}
            onChange={setSelectedDate}
            dateFormat="yyyy-MM-dd"
            locale={ko}
            maxDate={new Date()}
            customInput={<input className="date-input" />}
          />
          <CSVLink className="csv-download" data={csvData} filename={`ev_data.csv`}>
            CSV 다운로드
          </CSVLink>
        </Space>
        <Card><div style={{ height: '300px' }}><Line data={upperData} options={chartOptions} /></div></Card>
        <Card><div style={{ height: '300px' }}><Line data={lowerData} options={chartOptions} /></div></Card>
      </Space>
    </Card>
  );
};

export default EvMeasurementLineChart;
